import { useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import { MiChip, MiInputTextbox } from "@miplatform/components";
import { OPTION_STATUS, PLAN_CONFIG_STATUS } from "@miplatform/constants";
import { toUpperCase } from "@miplatform/utils";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { MaterialCard } from "../../../../../components/MaterialCard/MaterialCard";
import { useImportPlanAreaOptionsViewModel } from "./useImportPlanAreaOptionsViewModel";

export const ImportPlanAreaOptions = ({ area = {}, stateManager }) => {
  const isAreaOptionRemoved = area.optionStatus === OPTION_STATUS.REMOVED;
  const isAreaOptionAdded = area.optionStatus === OPTION_STATUS.ADDED;

  const { materials, getMaterials, setMaterials } =
    useImportPlanAreaOptionsViewModel({
      area,
      stateManager,
    });
  const { classes, theme } = useStyles({
    isAreaOptionRemoved,
    isAreaOptionAdded,
  });

  useEffect(() => {
    if (area.materials.length > 0) {
      getMaterials();
    } else {
      setMaterials([]);
    }
  }, [area.materials]);

  const isMaterialRemoved = (material) =>
    material.optionStatus === OPTION_STATUS.REMOVED ||
    material.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED;

  const getMaterialBgColor = (material) => {
    if (
      material.optionStatus === OPTION_STATUS.REMOVED ||
      material.planConfigStatus === PLAN_CONFIG_STATUS.REMOVED
    ) {
      return theme.palette.red.light;
    }
    if (
      material.optionStatus === OPTION_STATUS.ADDED ||
      material.planConfigStatus === PLAN_CONFIG_STATUS.ADDED
    ) {
      return theme.palette.green.light;
    }
    return theme.palette.common.white;
  };

  return (
    <Accordion className={classes.accordionContainer} expanded disableGutters>
      <AccordionSummary className={classes.accordionSummary}>
        <Box className={classes.areaActionsContainer}>
          <Typography className={classes.areaLabel}>{area.label}</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box className={classes.materialsContainer}>
          {materials.map((material) => {
            return (
              <Box className={classes.materialItemContainer} key={material.id}>
                {material.removalReason ? (
                  <RemovedReasonCard material={material} disabled />
                ) : (
                  <MaterialCard
                    key={material.id}
                    item={material}
                    isSuppliedByBuilder={material.suppliedByBuilder}
                    price={material.manufacturerListPrice}
                    fieldNote={material.fieldNote}
                    isRemoved={isMaterialRemoved(material)}
                    customBgColor={getMaterialBgColor(material)}
                    showPrice
                    leftAction={
                      <TextField
                        value={material.quantity}
                        className={classes.textFieldSmall}
                        size="small"
                        disabled
                      />
                    }
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const RemovedReasonCard = ({ material, disabled }) => {
  const { classes } = useStyles({
    isMaterialRemovalReason: material.removalReason,
  });

  return (
    <Box className={classes.removedCardContainer}>
      <Box>
        <TextField
          value={material.quantity}
          className={classes.textFieldSmall}
          size="small"
          disabled={disabled}
        />
      </Box>
      <Box className={classes.removedCardInfoContainer}>
        <Tooltip
          title={`${material.category.itemCode} | ${material.category.itemDescription}`}
        >
          <span>
            <MiChip
              label={toUpperCase(material.category.categoryName)}
              size="small"
              className={classes.chip}
            />
          </span>
        </Tooltip>
        <Box className={classes.removedCardReasonContainer}>
          <MiInputTextbox
            value={material.removalReason}
            label="Reason"
            disabled={disabled}
          />
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles()(
  (
    theme,
    { isAreaOptionRemoved, isAreaOptionAdded, isMaterialRemovalReason } = {}
  ) => {
    const getBgColor = () => {
      if (isAreaOptionRemoved) {
        return theme.palette.red.light;
      }
      if (isAreaOptionAdded) {
        return theme.palette.green.light;
      }
      return theme.palette.common.white;
    };

    const removalReasonCardBgColor = isMaterialRemovalReason
      ? theme.palette.red.light
      : theme.palette.common.white;

    return {
      accordionContainer: {
        backgroundColor: getBgColor(),
        width: "100%",
        margin: 0,
        padding: theme.spacing(1),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        borderRadius: 4,
        "&::before": {
          content: "none",
        },
        "&.Mui-expanded": {
          margin: 0,
        },
        "& .MuiAccordion-heading": {
          margin: 0,
          alignItems: "center",
          width: "100%",
          flexDirection: "column",
        },
        "& .MuiAccordionSummary-content": {
          margin: 0,
          alignItems: "center",
          width: "100%",
          flexDirection: "column",
        },
        "& .MuiCollapse-root": {
          width: "100%",
        },
      },
      areaLabel: {
        fontWeight: theme.fontWeight.semiBold,
        textDecoration: !isAreaOptionRemoved ? "none" : "line-through",
        color: !isAreaOptionRemoved
          ? theme.palette.black.main
          : theme.palette.red.dark,
      },
      accordionSummary: {
        flexDirection: "column",
        paddingTop: theme.spacing(1),
        "&.Mui-focusVisible": {
          backgroundColor: theme.palette.common.white,
        },
      },
      areaActionsContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        flex: 1,
      },
      chip: {
        borderRadius: 4,
      },
      materialsContainer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        flexDirection: "column",
      },
      materialItemContainer: {
        width: "100%",
        display: "flex",
      },
      removedCardContainer: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        border: `1px solid ${theme.palette.grey.accent}`,
        width: "100%",
        padding: theme.spacing(2, 1, 2, 1),
        borderRadius: 4,
        boxShadow: `0px 2px 2px ${theme.palette.grey.accent}`,
        backgroundColor: removalReasonCardBgColor,
      },
      removedCardInfoContainer: {
        display: "flex",
        gap: theme.spacing(1),
        alignItems: "center",
        width: "100%",
      },
      removedCardReasonContainer: {
        width: "100%",
        display: "flex",
      },
      textFieldSmall: {
        width: 50,
      },
    };
  }
);
